/**
 * Sandbox-only connect service for the developer portal.
 * Direct fetch — no SDK, never touches the browser storage layer.
 * Every call takes an explicit bearer token so the component owns the session in memory.
 */

import { ETwoFAMethod } from '@xeppt/xeppt-sdk/types';

import { ConnectApiError } from './connect_service';

import type {
    ConnectSession,
    ConnectConsentResponse,
    ConnectChargeResult
} from './connect_service';

export type { ConnectSession, ConnectConsentResponse, ConnectChargeResult };
export { ConnectApiError };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function bearerHeaders(accessToken: string): Record<string, string> {
    return { Authorization: `Bearer ${accessToken}` };
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        let code: string | undefined;
        try {
            const body = (await response.json()) as { code?: string };
            code = typeof body?.code === 'string' ? body.code : undefined;
        } catch {
            // Ignore JSON parse failures on error bodies.
        }
        throw new ConnectApiError(`HTTP ${response.status}`, response.status, code);
    }
    return response.json() as Promise<T>;
}

export type SandboxLoginResult =
    | { mfaRequired: false; accessToken: string }
    | { mfaRequired: true; twoFaToken: string; method: ETwoFAMethod; hint?: string };

export const sandboxConnectService = {
    /**
     * POST /auth/login — 200 returns tokens, 202 returns MFA challenge.
     * Access token is returned to the caller; the caller holds it in memory only.
     */
    async login(email: string, password: string): Promise<SandboxLoginResult> {
        const response = await fetch(`${BACKEND_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email_or_phone: email, password })
        });
        if (response.status === 202) {
            const body = (await response.json()) as {
                two_fa_requires?: boolean;
                two_fa_token?: string;
                // Backend sends the 2FA method as a numeric byte (see login.go).
                two_fa_method?: number;
                two_fa_hint?: string;
            };
            return {
                mfaRequired: true,
                twoFaToken: body.two_fa_token ?? '',
                method: (body.two_fa_method as ETwoFAMethod | undefined) ?? ETwoFAMethod.EMAIL,
                hint: body.two_fa_hint
            };
        }
        const data = await parseResponse<{ access_token: string }>(response);
        return { mfaRequired: false, accessToken: data.access_token };
    },

    /** POST /auth/login/verify — exchanges the MFA code for an access token. */
    async verifyMfa(twoFaToken: string, code: string): Promise<{ accessToken: string }> {
        const response = await fetch(`${BACKEND_URL}/auth/login/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: twoFaToken, code })
        });
        const data = await parseResponse<{ access_token: string }>(response);
        return { accessToken: data.access_token };
    },

    /** GET /connect/session?token=... — fetch the connect session metadata. */
    async getSession(connectToken: string, accessToken: string): Promise<ConnectSession> {
        const response = await fetch(
            `${BACKEND_URL}/connect/session?token=${encodeURIComponent(connectToken)}`,
            { headers: bearerHeaders(accessToken) }
        );
        return parseResponse<ConnectSession>(response);
    },

    /** POST /connect/consent — grant the identity consent and receive a one-time code. */
    async consent(connectToken: string, accessToken: string): Promise<ConnectConsentResponse> {
        const response = await fetch(`${BACKEND_URL}/connect/consent`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...bearerHeaders(accessToken)
            },
            body: JSON.stringify({ connect_token: connectToken })
        });
        return parseResponse<ConnectConsentResponse>(response);
    },

    /**
     * POST /connect/charge — charge the customer's XEPPT wallet.
     * On non-ok responses, parses the machine-readable `code` (e.g. INSUFFICIENT_FUNDS)
     * and throws a ConnectApiError so the caller can branch on it.
     */
    async charge(connectToken: string, accessToken: string): Promise<ConnectChargeResult> {
        const response = await fetch(`${BACKEND_URL}/connect/charge`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...bearerHeaders(accessToken)
            },
            body: JSON.stringify({ connect_token: connectToken })
        });
        if (!response.ok) {
            let code: string | undefined;
            try {
                const body = (await response.json()) as { code?: string };
                code = typeof body?.code === 'string' ? body.code : undefined;
            } catch {
                // Ignore JSON parse failures on error bodies.
            }
            throw new ConnectApiError(`HTTP ${response.status}`, response.status, code);
        }
        return response.json() as Promise<ConnectChargeResult>;
    },

    /** GET /profile — fetch the signed-in user's display name and avatar URL. */
    async getProfile(accessToken: string): Promise<{ firstName: string; lastName: string; avatar: string | null }> {
        const response = await fetch(`${BACKEND_URL}/profile`, {
            method: 'GET',
            headers: bearerHeaders(accessToken)
        });
        const data = await parseResponse<{
            profile?: { first_name?: string; last_name?: string; avatar?: string | null };
        }>(response);
        return {
            firstName: data.profile?.first_name ?? '',
            lastName: data.profile?.last_name ?? '',
            avatar: data.profile?.avatar ?? null
        };
    }
};
